import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_LOGIN_ROUTE, MAIN_ROUTE, ROLES } from "@/utils/constants.js";
import { setSessionExpiredHandler, AUTH_TOKEN_REFRESHED_EVENT } from "./session.js";
import { isTokenExpired } from "./token.js";

const AuthContext = createContext(null);

function readStoredToken() {
    const storedToken = localStorage.getItem("token");
    if (storedToken && isTokenExpired(storedToken)) {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        return null;
    }
    return storedToken;
}

function readStoredRole() {
    const storedToken = localStorage.getItem("token");
    if (!storedToken || isTokenExpired(storedToken)) {
        return null;
    }
    return localStorage.getItem("role");
}

export function AuthProvider({ children }) {
    const [token, setToken] = useState(readStoredToken);
    const [role, setRole] = useState(readStoredRole);
    const [loginRequired, setLoginRequired] = useState(() => {
        const storedToken = localStorage.getItem("token");
        return !!(storedToken && isTokenExpired(storedToken));
    });
    const navigate = useNavigate();

    const logout = useCallback(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        setToken(null);
        setRole(null);
    }, []);

    const handleSessionExpired = useCallback(() => {
        const previousRole = localStorage.getItem("role");
        logout();
        setLoginRequired(true);
        navigate(previousRole === ROLES.CUSTOMER ? MAIN_ROUTE : ADMIN_LOGIN_ROUTE, { replace: true });
    }, [logout, navigate]);

    useEffect(() => {
        setSessionExpiredHandler(handleSessionExpired);
        return () => setSessionExpiredHandler(null);
    }, [handleSessionExpired]);

    useEffect(() => {
        const onTokenRefreshed = (event) => {
            const next = event?.detail;
            if (typeof next === "string" && next.length > 0) {
                setToken(next);
            }
        };
        window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
        return () => window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    }, []);

    useEffect(() => {
        if (token && isTokenExpired(token)) {
            handleSessionExpired();
        }
    }, [token, handleSessionExpired]);

    useEffect(() => {
        if (!token) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            if (isTokenExpired(token)) {
                handleSessionExpired();
            }
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [token, handleSessionExpired]);

    const login = (newRole, newToken) => {
        localStorage.setItem("token", newToken);
        localStorage.setItem("role", newRole);
        setToken(newToken);
        setRole(newRole);
        setLoginRequired(false);
    };

    const clearLoginRequired = () => setLoginRequired(false);

    const isAuthenticated = !!token && !isTokenExpired(token);

    return (
        <AuthContext.Provider
            value={{
                token,
                role,
                isAuthenticated,
                login,
                logout,
                loginRequired,
                clearLoginRequired
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
